import { hasGroups, hasParentTopology } from "./canvas-groups";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { captureCanvasSelection, clipboardContentError, readCanvasClipboard, type CanvasClipboard } from "./canvas-clipboard";
import { cloneCanvasSelection } from "./canvas-clone";
import { removeElements, type CanvasGraph, type GraphCommand } from "./canvas-commands";
import { authoredGraph, sameGraph } from "./canvas-history";

export type ClipboardAction = "copy" | "cut" | "paste" | "duplicate";
export type CanvasClipboardActions = {
  busy: boolean;
  available: Record<ClipboardAction, boolean>;
  copy: () => Promise<void>;
  cut: () => Promise<void>;
  paste: () => Promise<void>;
  duplicate: () => void;
};
export type ClipboardAdapter = { readText?: () => Promise<string>; writeText?: (text: string) => Promise<void> };
const browserClipboard: ClipboardAdapter = {
  readText: () => {
    if (!navigator.clipboard?.readText) throw new Error("Clipboard reading is unavailable");
    return navigator.clipboard.readText();
  },
  writeText: text => {
    if (!navigator.clipboard?.writeText) throw new Error("Clipboard writing is unavailable");
    return navigator.clipboard.writeText(text);
  },
};

export function useCanvasClipboard(options: {
  route: string; generation: number; canRead: boolean; canEdit: boolean; hasSelection: boolean;
  getGeneration: () => number; isReadable: () => boolean; isEditable: () => boolean;
  getVisibleGraph: () => CanvasGraph; getWorkingGraph: () => CanvasGraph;
  execute: (command: GraphCommand) => void;
  placement: (payload: CanvasClipboard, action: "paste" | "duplicate") => { x: number; y: number } | null;
  adapter?: ClipboardAdapter; idFactory?: (kind: "node" | "edge" | "group") => string;
}): CanvasClipboardActions {
  const current = useRef(options); current.current = options;
  const mounted = useRef(false), lifecycle = useRef(0), pending = useRef(false);
  const [busy, setBusy] = useState(false);
  useLayoutEffect(() => {
    mounted.current = true; lifecycle.current++;
    return () => { mounted.current = false; lifecycle.current++; };
  }, []);
  // A lock that was entered and subsequently left cannot revive a pending request.
  useLayoutEffect(() => { lifecycle.current++; }, [options.route, options.generation, options.canRead, options.canEdit]);

  const begin = useCallback((action: ClipboardAction) => {
    const o = current.current;
    if (!mounted.current || pending.current || !o.isReadable() || (action !== "copy" && !o.isEditable()) ||
      (action !== "paste" && !o.getVisibleGraph().nodes.some(node => node.selected))) return null;
    pending.current = true; setBusy(true);
    const identity = lifecycle.current, route = o.route, generation = o.getGeneration();
    return {
      options: o,
      valid: () => mounted.current && lifecycle.current === identity && current.current.route === route &&
        current.current.getGeneration() === generation && current.current.isReadable() &&
        (action === "copy" || current.current.isEditable()),
      finish: () => { pending.current = false; if (mounted.current) setBusy(false); },
    };
  }, []);

  const write = useCallback(async (action: "copy" | "cut") => {
    const op = begin(action);
    if (!op) return;
    try {
      const captured = captureCanvasSelection(op.options.getVisibleGraph());
      if (!captured.ok) { toast.error(clipboardContentError(captured.reason)); return; }
      const before = action === "cut" ? authoredGraph(op.options.getWorkingGraph()) : null;
      const adapter = op.options.adapter ?? browserClipboard;
      if (!adapter.writeText) throw new Error("Clipboard writing is unavailable");
      await adapter.writeText(JSON.stringify(captured.value));
      if (!op.valid()) return;
      if (before) {
        if (!sameGraph(before, current.current.getWorkingGraph())) {
          toast.info("Cut cancelled because the diagram changed. Your content is still here."); return;
        }
        op.options.execute(removeElements(captured.value.nodes.map(node => node.id)));
      }
      toast.success(action === "copy" ? "Selection copied" : "Selection cut");
    } catch {
      if (op.valid()) toast.error("Clipboard access failed. Allow clipboard access in your browser and try again. Your content is still here.");
    } finally { op.finish(); }
  }, [begin]);

  const insert = useCallback((payload: CanvasClipboard, action: "paste" | "duplicate", execute: (command: GraphCommand) => void) => {
    const o = current.current;
    const translation = o.placement(payload, action);
    if (!translation) { toast.error("The canvas is not visible. Open the diagram and try again."); return; }
    if (hasGroups(payload) && o.getWorkingGraph().nodes.some(hasParentTopology)) {
      toast.error("Grouped content cannot be pasted into a diagram with parent containers."); return;
    }
    const command = cloneCanvasSelection(payload, o.getWorkingGraph(), translation,
      o.idFactory ?? (kind => `${kind}_${crypto.randomUUID()}`));
    if (!command) { toast.error("Could not create fresh copies. Try again."); return; }
    execute(command);
    toast.success(action === "paste" ? "Selection pasted" : "Selection duplicated");
  }, []);

  const paste = useCallback(async () => {
    const op = begin("paste");
    if (!op) return;
    try {
      const adapter = op.options.adapter ?? browserClipboard;
      if (!adapter.readText) throw new Error("Clipboard reading is unavailable");
      const text = await adapter.readText();
      if (!op.valid()) return;
      const payload = readCanvasClipboard(text);
      if (!payload.ok) { toast.error(clipboardContentError(payload.reason)); return; }
      insert(payload.value, "paste", op.options.execute);
    } catch {
      if (op.valid()) toast.error("Clipboard access failed. Allow clipboard access in your browser and try again.");
    } finally { op.finish(); }
  }, [begin, insert]);

  const duplicate = useCallback(() => {
    const op = begin("duplicate");
    if (!op) return;
    try {
      const payload = captureCanvasSelection(op.options.getVisibleGraph());
      if (!payload.ok) { toast.error(clipboardContentError(payload.reason)); return; }
      if (op.valid()) insert(payload.value, "duplicate", op.options.execute);
    } finally { op.finish(); }
  }, [begin, insert]);

  return { busy, available: {
    copy: !busy && options.canRead && options.hasSelection,
    cut: !busy && options.canEdit && options.hasSelection,
    paste: !busy && options.canEdit,
    duplicate: !busy && options.canEdit && options.hasSelection,
  }, copy: useCallback(() => write("copy"), [write]), cut: useCallback(() => write("cut"), [write]), paste, duplicate };
}
